/**
 * Conversations API Resource
 *
 * Handles retrieving conversations and messages between users and Pages/Instagram Business accounts.
 *
 * @see https://developers.facebook.com/docs/messenger-platform/conversations
 */

import type { HTTPClient, APIOptions } from '../core/http-client.js';
import { MessengerConfigError } from '../core/errors.js';
import type {
  ListConversationsParams,
  ListConversationsResponse,
  GetConversationParams,
  ConversationDetail,
  GetMessageParams,
  Message,
  ListMessagesResponse,
} from '../types/conversations.js';

/**
 * Conversations API class for retrieving conversation and message data
 *
 * @example
 * ```typescript
 * const messenger = new Messenger({ accessToken: 'PAGE_TOKEN' });
 *
 * // List all conversations
 * const conversations = await messenger.conversations.list('PAGE_ID', {
 *   platform: 'messenger'
 * });
 *
 * // Get conversation details
 * const conversation = await messenger.conversations.get('CONVERSATION_ID');
 *
 * // Get message details
 * const message = await messenger.conversations.getMessage('MESSAGE_ID');
 * ```
 */
export class ConversationsAPI {
  constructor(private httpClient: HTTPClient) {}

  /**
   * Get a list of conversations for a Page or Instagram Business Account
   *
   * @param pageId - The Page ID or Instagram Business Account ID
   * @param params - Optional parameters for filtering and pagination
   * @param options - Optional request options (e.g., token override)
   * @returns List of conversations
   *
   * @example
   * ```typescript
   * // List Messenger conversations
   * const conversations = await messenger.conversations.list('PAGE_ID', {
   *   platform: 'messenger',
   *   limit: 25
   * });
   *
   * // Find conversation with specific user
   * const userConvos = await messenger.conversations.list('PAGE_ID', {
   *   platform: 'instagram',
   *   user_id: 'USER_INSTAGRAM_SCOPED_ID'
   * });
   *
   * // Paginate through conversations
   * const nextPage = await messenger.conversations.list('PAGE_ID', {
   *   platform: 'messenger',
   *   after: conversations.paging?.cursors?.after
   * });
   * ```
   */
  async list(
    pageId: string,
    params?: ListConversationsParams,
    options?: APIOptions
  ): Promise<ListConversationsResponse> {
    if (!pageId) {
      throw new MessengerConfigError('Page ID is required');
    }

    const queryParams: Record<string, string> = {};

    if (params?.platform) {
      queryParams.platform = params.platform;
    }

    if (params?.user_id) {
      queryParams.user_id = params.user_id;
    }

    if (params?.folder) {
      queryParams.folder = params.folder;
    }

    if (params?.limit) {
      queryParams.limit = params.limit.toString();
    }

    if (params?.after) {
      queryParams.after = params.after;
    }

    if (params?.before) {
      queryParams.before = params.before;
    }

    return this.httpClient.request<ListConversationsResponse>({
      method: 'GET',
      path: `/${pageId}/conversations`,
      query: queryParams,
      accessToken: options?.accessToken,
    });
  }

  /**
   * Get details about a specific conversation
   *
   * @param conversationId - The conversation ID
   * @param params - Optional parameters for fields and pagination
   * @param options - Optional request options (e.g., token override)
   * @returns Conversation details
   *
   * @example
   * ```typescript
   * // Get conversation with messages
   * const conversation = await messenger.conversations.get('CONVERSATION_ID', {
   *   fields: ['messages', 'participants']
   * });
   *
   * // Get only participants
   * const participants = await messenger.conversations.get('CONVERSATION_ID', {
   *   fields: ['participants']
   * });
   * ```
   */
  async get(
    conversationId: string,
    params?: GetConversationParams,
    options?: APIOptions
  ): Promise<ConversationDetail> {
    if (!conversationId) {
      throw new MessengerConfigError('Conversation ID is required');
    }

    const queryParams: Record<string, string> = {};

    if (params?.fields && params.fields.length > 0) {
      queryParams.fields = params.fields.join(',');
    }

    if (params?.limit) {
      queryParams.limit = params.limit.toString();
    }

    if (params?.after) {
      queryParams.after = params.after;
    }

    if (params?.before) {
      queryParams.before = params.before;
    }

    return this.httpClient.request<ConversationDetail>({
      method: 'GET',
      path: `/${conversationId}`,
      query: queryParams,
      accessToken: options?.accessToken,
    });
  }

  /**
   * Get messages in a conversation
   *
   * @param conversationId - The conversation ID
   * @param params - Optional parameters for pagination
   * @param options - Optional request options (e.g., token override)
   * @returns List of messages
   *
   * @example
   * ```typescript
   * // Get messages in a conversation
   * const messages = await messenger.conversations.getMessages('CONVERSATION_ID', {
   *   limit: 20
   * });
   *
   * // Paginate through messages
   * const nextPage = await messenger.conversations.getMessages('CONVERSATION_ID', {
   *   after: messages.paging?.cursors?.after
   * });
   * ```
   */
  async getMessages(
    conversationId: string,
    params?: { limit?: number; after?: string; before?: string },
    options?: APIOptions
  ): Promise<ListMessagesResponse> {
    if (!conversationId) {
      throw new MessengerConfigError('Conversation ID is required');
    }

    const queryParams: Record<string, string> = {
      fields: 'messages',
    };

    if (params?.limit) {
      queryParams.limit = params.limit.toString();
    }

    if (params?.after) {
      queryParams.after = params.after;
    }

    if (params?.before) {
      queryParams.before = params.before;
    }

    const response = await this.httpClient.request<ConversationDetail>({
      method: 'GET',
      path: `/${conversationId}`,
      query: queryParams,
      accessToken: options?.accessToken,
    });

    return {
      data: response.messages?.data || [],
      paging: response.messages?.paging,
    };
  }

  /**
   * Get details about a specific message
   *
   * Note: You can only retrieve details for the 20 most recent messages in a conversation.
   * Older messages will return an error indicating the message was deleted.
   *
   * @param messageId - The message ID
   * @param params - Optional parameters for fields to retrieve
   * @param options - Optional request options (e.g., token override)
   * @returns Message details
   *
   * @example
   * ```typescript
   * // Get full message details
   * const message = await messenger.conversations.getMessage('MESSAGE_ID', {
   *   fields: ['id', 'created_time', 'from', 'to', 'message', 'attachments', 'reactions']
   * });
   *
   * // Get basic message info
   * const basicMessage = await messenger.conversations.getMessage('MESSAGE_ID');
   * ```
   */
  async getMessage(
    messageId: string,
    params?: GetMessageParams,
    options?: APIOptions
  ): Promise<Message> {
    if (!messageId) {
      throw new MessengerConfigError('Message ID is required');
    }

    const queryParams: Record<string, string> = {};

    if (params?.fields && params.fields.length > 0) {
      queryParams.fields = params.fields.join(',');
    } else {
      // Default fields for message details
      queryParams.fields = 'id,created_time,from,to,message,attachments,reactions,reply_to';
    }

    return this.httpClient.request<Message>({
      method: 'GET',
      path: `/${messageId}`,
      query: queryParams,
      accessToken: options?.accessToken,
    });
  }

  /**
   * Get the 20 most recent messages in a conversation with full details
   *
   * This is a convenience method that retrieves message IDs and then fetches
   * full details for each message. Note that only the 20 most recent messages
   * can have their details retrieved.
   *
   * @param conversationId - The conversation ID
   * @param options - Optional request options (e.g., token override)
   * @returns Array of detailed messages
   *
   * @example
   * ```typescript
   * // Get recent messages with full details
   * const messages = await messenger.conversations.getRecentMessages('CONVERSATION_ID');
   *
   * for (const message of messages) {
   *   console.log(`${message.from?.username}: ${message.message}`);
   * }
   * ```
   */
  async getRecentMessages(
    conversationId: string,
    options?: APIOptions
  ): Promise<Message[]> {
    // First, get the list of message IDs (limited to 20)
    const messagesList = await this.getMessages(
      conversationId,
      { limit: 20 },
      options
    );

    // Then fetch details for each message
    const messagePromises = messagesList.data.map((msg) =>
      this.getMessage(msg.id, undefined, options)
    );

    return Promise.all(messagePromises);
  }

  /**
   * Find a conversation between a Page and a specific user
   *
   * This is a convenience method that finds a conversation with a specific user.
   *
   * @param pageId - The Page ID or Instagram Business Account ID
   * @param userId - The user's Instagram-scoped ID or Page-scoped ID
   * @param platform - The platform ('instagram' or 'messenger')
   * @param options - Optional request options (e.g., token override)
   * @returns The conversation ID, or null if not found
   *
   * @example
   * ```typescript
   * // Find conversation with Instagram user
   * const conversationId = await messenger.conversations.findByUser(
   *   'PAGE_ID',
   *   'USER_INSTAGRAM_SCOPED_ID',
   *   'instagram'
   * );
   *
   * if (conversationId) {
   *   const messages = await messenger.conversations.getRecentMessages(conversationId);
   * }
   * ```
   */
  async findByUser(
    pageId: string,
    userId: string,
    platform: 'instagram' | 'messenger',
    options?: APIOptions
  ): Promise<string | null> {
    const response = await this.list(
      pageId,
      {
        platform,
        user_id: userId,
      },
      options
    );

    if (response.data && response.data.length > 0 && response.data[0]) {
      return response.data[0].id;
    }

    return null;
  }
}
